import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Mail, Phone, MapPin, Shield, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ProfilePictureUpload from './ProfilePictureUpload';

interface AccountSettingsProps {
  onBack: () => void;
  isInitialSetup?: boolean;
  onProfileComplete?: () => void;
}

export default function AccountSettings({ onBack, isInitialSetup = false, onProfileComplete }: AccountSettingsProps) {
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    country: ''
  });

  useEffect(() => {
    // Fetch existing profile data if not initial setup
    if (!isInitialSetup) {
      fetchProfileData();
    }
  }, [isInitialSetup]);

  const fetchProfileData = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error) throw error;

      if (profile) {
        setFormData(prev => ({
          ...prev,
          phone: profile.phone || '',
          address: profile.address || '',
          city: profile.city || '',
          country: profile.country || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsUpdating(true);

    try {
      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user?.id,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country
        });

      if (profileError) throw profileError;

      setMessage({
        type: 'success',
        text: 'Profile updated successfully'
      });

      // If this is initial setup, notify parent
      if (isInitialSetup && onProfileComplete) {
        onProfileComplete();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to update profile'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={isInitialSetup ? '' : "fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-start justify-end p-4"}>
      <div className={`bg-white/95 backdrop-blur-md w-full ${isInitialSetup ? 'max-w-full' : 'max-w-md'} rounded-2xl shadow-2xl border border-white/20 overflow-hidden animate-slideIn`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            {!isInitialSetup && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold">{isInitialSetup ? 'Complete Your Profile' : 'Account Settings'}</h2>
              <p className="text-gray-600">
                {isInitialSetup 
                  ? 'Please provide your information to complete your profile' 
                  : 'Manage your account settings'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Profile Picture */}
          <div className="flex flex-col items-center">
            <ProfilePictureUpload
              currentUrl={user?.profile_picture}
              onUpload={async (url) => {
                try {
                  const { error } = await supabase
                    .from('users')
                    .update({ profile_picture: url })
                    .eq('id', user?.id);

                  if (error) throw error;

                  setMessage({
                    type: 'success',
                    text: 'Profile picture updated successfully'
                  });

                  setTimeout(() => setMessage(null), 3000);
                } catch (error) {
                  setMessage({
                    type: 'error',
                    text: 'Failed to update profile picture'
                  });
                }
              }}
              onError={(error) => setMessage({ type: 'error', text: error })}
            />
          </div>

          {/* Status Message */}
          {message && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            } flex items-center gap-2`}>
              {message.type === 'success' ? (
                <Check size={18} className="flex-shrink-0" />
              ) : (
                <Shield size={18} className="flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  disabled={!!user?.name}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  disabled={!!user?.email}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required={isInitialSetup}
                />
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required={isInitialSetup}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required={isInitialSetup}
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  required={isInitialSetup}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isUpdating}
              className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUpdating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Updating...</span>
                </>
              ) : (
                isInitialSetup ? 'Complete Profile' : 'Save Changes'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}